import { yupResolver } from "@hookform/resolvers/yup";
import cx from "classnames";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { canv } from "@/canv/global";
import { useDebounceValue } from "@/helpers/hooks/useDebounceValue";
import { AddMemberIcon, CloseIcon2 } from "@/icons";
import { getProjectSelectOptions } from "@/interface/ShareWindow/constants";
import { PermissionSelect } from "@/interface/ShareWindow/PermissionSelect/PermissionSelect";
import { TeamMember } from "@/interface/ShareWindow/TeamMember/TeamMember";
import type { Member, MemberAccessInterface } from "@/interface/ShareWindow/types";
import { AccessEnum } from "@/interface/ShareWindow/types";
import { Button } from "@/shared/ui/Button";
import styles from "./SearchMember.module.scss";

interface SearchMemberProps {
    toggleVisibility: (b: boolean) => void;
    onClose: () => void;
    callback: ({ members, access }: MemberAccessInterface) => void;
    teamId: number;
}

interface FormValues {
    member?: string;
}

export const SearchMember = ({
    toggleVisibility,
    onClose,
    callback,
    teamId,
}: SearchMemberProps) => {
    const { t } = useTranslation();
    const projectSelectOptions = getProjectSelectOptions();
    const [emails, setEmail] = useState<Member[]>([]);
    const [accessStatus, setAccessStatus] = useState(AccessEnum.edit);
    const selectedValue =
        projectSelectOptions.find((option) => option.value === accessStatus) ||
        projectSelectOptions[0];

    const schema = yup.object().shape({
        member: yup.string(),
    });

    const { control, watch } = useForm<FormValues>({
        resolver: yupResolver(schema),
        defaultValues: {
            member: "",
        },
    });

    const value = watch("member");
    const debouncedValue = useDebounceValue(value, 500);

    useEffect(() => {
        if (emails.length === 0 && !debouncedValue) {
            toggleVisibility(false);
        }
        if (emails.length > 0 || (debouncedValue && debouncedValue?.length > 0)) {
            toggleVisibility(true);
        }
    }, [debouncedValue, emails, toggleVisibility]);

    const showTeamMember = emails.length > 0 || (debouncedValue && debouncedValue.length > 0);

    const onDelete = (email: string) => {
        setEmail(emails.filter((item) => item.email !== email));
    };

    const handleInvite = async () => {
        if (emails.length > 0) {
            callback({ members: emails.map((item) => item.email), access: accessStatus });
        }
    };

    return (
        <div className={styles.searchMember}>
            <div className={styles.shareWindowSearch}>
                <AddMemberIcon />

                <div className={styles.shareWindowSearchField}>
                    <Controller
                        name={"member"}
                        control={control}
                        render={({ field: { onChange, value, ref } }) => (
                            <input
                                ref={ref}
                                autoComplete="off"
                                className={styles.shareWindowSearchFieldInput}
                                type="text"
                                placeholder={t("share.inviteBoard")}
                                value={value}
                                onChange={onChange}
                                onFocus={() => {
                                    canv.appInterface.inputFocused = 1;
                                }}
                                onBlur={() => {
                                    canv.appInterface.inputFocused = 0;
                                }}
                            />
                        )}
                    />
                </div>
                {showTeamMember && (
                    <div className={styles.blueSelect}>
                        <PermissionSelect<AccessEnum>
                            options={projectSelectOptions}
                            onChange={(e) => {
                                setAccessStatus(e.value as AccessEnum);
                            }}
                            selectedValue={selectedValue}
                        />
                    </div>
                )}
            </div>

            {showTeamMember && (
                <>
                    <div className={cx(styles.emails, emails.length && styles.topPadding)}>
                        {emails.map((item) => (
                            <button
                                type="button"
                                key={item.email}
                                className={styles.selectedMember}
                                onClick={() => onDelete(item.email)}
                            >
                                <div className={styles.logo}>{item.name.substring(0, 1)}</div>
                                <div className={styles.name}>{item.name.split(" ")?.[0]}</div>
                                <div>
                                    <CloseIcon2 />
                                </div>
                            </button>
                        ))}
                    </div>

                    <div
                        className={[
                            styles.shareWindowPeople,
                            showTeamMember ? styles.fixedHeight : undefined,
                        ].join(" ")}
                    >
                        <p className={styles.title}>{t("share.teamMembers")}</p>
                        <TeamMember
                            teamId={teamId}
                            filter={debouncedValue || ""}
                            rawFilter={value || ""}
                            onSelect={(email: Member) => {
                                if (!emails.some((e) => e.email === email.email)) {
                                    setEmail([...emails, email]);
                                }
                            }}
                        />
                    </div>

                    <div className={styles.actions}>
                        <Button
                            className={styles.actionButtons}
                            onClick={handleInvite}
                            style={{ width: "123px" }}
                        >
                            {t("share.invite")}
                        </Button>

                        <Button
                            className={cx(styles.actionButtons, styles.outLine)}
                            onClick={onClose}
                            variant="tertriary"
                        >
                            {t("share.close")}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};
